import {
  ActiveCompany,
  CompanyAddress,
  ILoadedVenue,
  IUserCompanyInfoDTO,
} from '$models'
import logger from './logger'
import { PermissionType } from '../constants/enums'

export { PermissionType }
import { clearCompanySettingsCache } from './companySettingsCache'

// TODO - I'd like to rewrite this as a Zustand store that can be pulled into
// any component that needs the data managed here. Potentially with nested
// Zustand stores being put in place we can remove the need to reset the state
// in components when remounting to ensure we pick up the latest data from this
// store. See the ./store/appState.ts file for an example of how this could be
// done (they may end up being merged).

// Stores the current state of the app context. This is populated once we hear
// from the react bridge (to the AngularJS app) that it is ready to be read.
const CurrentAppContext: AppContextType = {
  ready: false,
  data: undefined,
}

/**
 * Pulls the current app context from the AngularJS bridge and marks the local
 * cache as ready. Safe to call when the bridge is absent (e.g. standalone mode
 * where there is no AngularJS host) - it simply does nothing.
 */
function pullAppContext(): void {
  const bridge = window.__Loaded?.reactBridge

  if (!bridge) {
    return
  }

  bridge.appContext().then((data) => {
    logger.debug('Received app context data %o', data)
    CurrentAppContext.ready = true
    CurrentAppContext.data = data
  })
}

// Starts listening for notifications from AngularJS that data is able to be
// read. Note that we continue listening as if the company changes in the
// AngularJS app we want to ensure the updated company data is being used in
// the React app too.
window.addEventListener('@@loaded/appContextSet', () => {
  logger.debug('Received notification the app context has been set')
  pullAppContext()
})

// The handshake with AngularJS is level-triggered as well as edge-triggered:
// `appContextSet` is a one-shot event, and AngularJS (loaded as a classic
// script) can dispatch it before this module (loaded as a module script) has
// run - a script-load race that would otherwise leave the React app
// unhydrated forever. AngularJS leaves a persistent `appContextSet` marker on
// the bridge when it dispatches the event (see app/startup/reactBridge.run.js),
// so if the event has already fired we pull the context immediately. Pulling
// twice (marker plus a late event) is harmless - it just re-reads the same
// data.
if (window.__Loaded?.reactBridge?.appContextSet) {
  logger.debug('App context was set before this module loaded - pulling now')
  pullAppContext()
}

// Also listen for notification of context changes and update the data too.
window.addEventListener('@@loaded/appContextChange', (ev: Event) => {
  const { type } = (ev as CustomEvent).detail
  logger.debug(
    'Received notification the app context %s data has changed',
    type
  )

  window.__Loaded?.reactBridge?.appContext().then((data) => {
    logger.debug('Received updated app context data %o', data)
    CurrentAppContext.data = data
  })
})

export function isReady() {
  // Already handshaked, no point starting an interval
  if (CurrentAppContext.ready) {
    return Promise.resolve()
  }

  const readyPromise = new Promise<void>((resolve) => {
    const interval = setInterval(() => {
      if (CurrentAppContext.ready) {
        logger.info('AppContext is now ready.')
        clearInterval(interval)
        resolve()
      }
    }, 15)
  })

  return readyPromise
}

/**
 * Returns the active company data and functions to read preference and settings
 * for that company.
 *
 * @returns
 */
export function getActiveCompany() {
  const activeCompany = CurrentAppContext.data?.activeCompany

  // There is no active company while the user is mid-signup (no companies
  // yet) or in standalone mode where there is no AngularJS-provided context.
  // Callers get null rather than an object full of undefined fields.
  if (!activeCompany) {
    return null
  }

  return {
    id: activeCompany.id,
    name: activeCompany.name,
    pos: activeCompany.pos,
    invoiceEmail: activeCompany.invoiceEmail,
    country: activeCompany.country,
    tier: activeCompany.subscriptionTier,
    address: activeCompany.address,
    hasPermission: checkPermission,
    preference: readPreference,
    setting: readSetting,
  } as ActiveCompany
}

/**
 * Returns the venues that are accesible by the Loaded User
 */
export function getAllVenues(): ILoadedVenue[] {
  if (!CurrentAppContext.data?.allCompanies) {
    return []
  }

  return CurrentAppContext.data.allCompanies.map((venue) => {
    const permissionsArray = venue.permissions
      ? venue.permissions.split(',')
      : []

    return {
      ...venue,
      shortName: venue.name,
      permissions: permissionsArray,
      hasPermission: (permissionName: PermissionType) =>
        checkVenuePermission(permissionsArray, permissionName),
    }
  })
}

/**
 * Whether a venue in the list has every one of these permissions. A venue that is not in the
 * list has none, because the user cannot reach it at all.
 */
export function venueHasPermissions(
  venues: ILoadedVenue[],
  venueId: string,
  permissions: PermissionType[]
): boolean {
  const venue = venues.find((candidate) => candidate.id === venueId)

  if (!venue) {
    return false
  }

  return permissions.every((permission) => venue.hasPermission(permission))
}

/**
 * Whether the user has every one of these permissions at the venue a call targets. A call with
 * no venue goes to the active company, so that is where it gets checked.
 */
export function hasPermissionsAtVenue(
  permissions: PermissionType[],
  venueId?: string
): boolean {
  const targetVenueId = venueId ?? getActiveCompany()?.id

  if (!targetVenueId) {
    return false
  }

  return venueHasPermissions(getAllVenues(), targetVenueId, permissions)
}

export function getUserDetails() {
  return {
    ...CurrentAppContext.data?.userDetails,
  }
}

/**
 * Whether the AngularJS app is currently in multisite mode (i.e. viewing a
 * multisite report). This is per-route and in-memory in AngularJS; React
 * re-reads it whenever the app context changes.
 */
export function getMultisiteEnabled(): boolean {
  return CurrentAppContext.data?.multisiteEnabled ?? false
}

/**
 * Re-reads the latest app context from the AngularJS bridge and refreshes the
 * cached data, resolving once it is up to date. Callers that need to react to a
 * context change (e.g. the app store updating its multisite state) can await
 * this so they read fresh values rather than racing the internal change
 * listener above.
 */
export function refreshAppContext(): Promise<void> {
  const bridge = window.__Loaded?.reactBridge

  if (!bridge) {
    return Promise.resolve()
  }

  return bridge.appContext().then((data) => {
    CurrentAppContext.data = data
  })
}

/* internal functions and types below here */

/**
 * Checks if a venue has a specific permission from its permissions array.
 * The check is case-insensitive and trims whitespace from permission names.
 *
 * @param permissionsArray - Array of permission strings for the venue
 * @param permissionName - The permission type to check for
 * @returns true if the venue has the specified permission, false otherwise
 */
function checkVenuePermission(
  permissionsArray: string[],
  permissionName: PermissionType
) {
  if (!permissionsArray || permissionsArray.length === 0) {
    return false
  }

  return permissionsArray.some(
    (permission) =>
      permission.toLowerCase().trim() === permissionName.toLowerCase()
  )
}

function checkPermission(permissionName: PermissionType) {
  if (!CurrentAppContext.ready) {
    logger.warn('AppContext is not ready')
    throw new Error(
      'Attempted to read a company permission before the AppContext is ready.'
    )
  }

  if (!CurrentAppContext.data) {
    logger.warn('AppContext is missing activeCompany data')
    throw new Error(
      'Attempted to read a company permission with no active company available.'
    )
  }

  const { activeCompany } = CurrentAppContext.data
  if (!activeCompany) {
    logger.warn('No active company in AppContext')
    return false
  }

  const { permissions } = activeCompany
  if (!permissions) {
    logger.warn('No permissions in active company')
    return false
  }

  return permissions.toLowerCase().includes(permissionName.toLowerCase())
}

function readPreference(preferenceName: CompanyPreferences) {
  if (!CurrentAppContext.ready) {
    logger.warn('AppContext is not ready')
    throw new Error(
      'Attempted to read a company preference before the AppContext is ready.'
    )
  }

  if (!CurrentAppContext.data) {
    logger.warn('AppContext is missing activeCompany data')
    throw new Error(
      'Attempted to read a company preference with no active company available.'
    )
  }

  const { activeCompany } = CurrentAppContext.data
  if (!activeCompany) {
    logger.warn('No active company in AppContext')
    return null
  }

  const { preferences } = activeCompany
  if (!preferences) {
    logger.warn('No preferences in active company')
    return null
  }

  const preference = preferences[preferenceName]
  if (!preference) {
    return null
  }

  return preference
}

function readSetting(settingName: VenueSettings) {
  if (!CurrentAppContext.ready) {
    logger.warn('AppContext is not ready')
    throw new Error(
      'Attempted to read a venue setting before the AppContext is ready.'
    )
  }

  if (!CurrentAppContext.data) {
    logger.warn('AppContext is missing activeCompany data')
    throw new Error(
      'Attempted to read a venue setting with no active company available.'
    )
  }

  const { activeCompany } = CurrentAppContext.data
  if (!activeCompany) {
    logger.warn('No active company in AppContext')
    return null
  }

  const { settings } = activeCompany
  if (!settings) {
    logger.warn('No settings in active company')
    return null
  }

  const settingValue = settings[settingName]
  if (!settingValue) {
    return null
  }

  return settingValue
}

/**
 * Updates a single company preference in the local app context so that
 * subsequent reads via `activeCompany.preference()` return the new value
 * without requiring a full page reload.
 */
export function updateVenuePreference(
  preferenceName: CompanyPreferences,
  value: unknown
): void {
  if (!CurrentAppContext.ready || !CurrentAppContext.data) {
    logger.warn(
      'Cannot update preference %s - AppContext is not ready',
      preferenceName
    )
    return
  }

  const { activeCompany } = CurrentAppContext.data
  if (!activeCompany?.preferences) {
    logger.warn(
      'Cannot update preference %s - no active company preferences',
      preferenceName
    )
    return
  }

  const preferences = activeCompany.preferences as unknown as Record<
    string,
    unknown
  >
  preferences[preferenceName] = value
  clearCompanySettingsCache()
  logger.debug('Updated local preference %s to %o', preferenceName, value)
}

// This code relies on reading a global variable that is defined in the AngularJS
// app. We specify it's shape here so that TS knows what to expect. The
// `reactBridge` is optional because in standalone mode the AngularJS host
// (which defines the bridge) does not exist at all - only the plain config
// object from the standalone index.html is present.
type LoadedGlobal = {
  reactBridge?: {
    appContext: () => Promise<AppContextData>
    // Persistent marker left by AngularJS when it dispatches the one-shot
    // `@@loaded/appContextSet` event, allowing a late-loading React bundle to
    // detect that the context is already available (see the module-load check
    // above).
    appContextSet?: boolean
  }
}
export interface LoadedWindow extends Window {
  __Loaded: LoadedGlobal
}
declare let window: LoadedWindow

/**
 * The company preferences that are avaiable.
 */
export enum CompanyPreferences {
  DayStart = 'date.daycutoff.daystart',
  WeekStart = 'date.daycutoff.weekstart',
  Locale = 'locale.locale',
  Province = 'locale.province',
  TimeZone = 'locale.timeZone',
  TimePermissionAllowsWageAndSalarySummary = 'permissions.timeAllowsSummary',
  DefaultFreightTaxRate = 'tax.defaultFreightRate',
  OverrideSalesTaxRate = 'tax.overrideSalesTax',
  ClockinRounding = 'time.clockinRounding',
  BreakRoundingTarget = 'time.breakRoundingTarget',
  BreakRoundingMinimum = 'time.breakRoundingMinimum',
  IncludePosClockins = 'time.includePosClockins',
  ExportClockinsOnly = 'time.exportClockinsOnly',
  PayrollSystem = 'time.payrollSystem',
  TimeRosterPublicToken = 'time.rosterPublicToken',
  XeroFreightAccount = 'xero.freightaccount',
  TeamAppEnabled = 'teamApp.enabled',
  TeamAppDisplayPayInformation = 'teamApp.displayPayInformation',
  TeamAppDisplayPhoneNumbers = 'teamApp.displayPhoneNumbers',
  TeamAppShiftSwapEnabled = 'teamApp.shiftSwapEnabled',
  TeamAppShiftSwapRequireManagerApproval = 'teamApp.shiftSwapRequireManagerApproval',
  TeamAppShiftSwapAllowOfferToEveryone = 'teamApp.shiftSwapAllowOfferToEveryone',
  TeamAppShiftSwapTransferCutoffMinutes = 'teamApp.shiftSwapTransferCutoffMinutes',
  TeamAppShiftSwapRestrictTransfers = 'teamApp.shiftSwapRestrictTransfers',
  TeamAppShiftSwapRestrictTransfersBy = 'teamApp.shiftSwapRestrictTransfersBy',
}

/**
 * The venue settings that are available.
 */
export enum VenueSettings {
  LaunchAppUrl = 'LaunchAppUrl',
}

type AppContextType = {
  ready: boolean
  data?: AppContextData
}

type AppContextData = {
  activeCompany: ActiveCompanyType
  userDetails: UserDetails
  allCompanies: IUserCompanyInfoDTO[]
  multisiteEnabled: boolean
}

type ActiveCompanyType = {
  id: string
  name: string
  preferences: CompanyPreferences
  settings: VenueSettings
  permissions: PermissionType
  pos: string
  invoiceEmail: string
  country: string
  subscriptionDenied: boolean
  subscriptionState: number
  subscriptionTier: string
  address: CompanyAddress
}

type UserDetails = {
  email: string
  firstName: string
  lastName: string
  hasPassword: boolean
  id: string
  isLegacyUser: boolean
  name: string
}
